import FormTextarea from '@c/forms/controls/FormTextarea';
import { SendIcon, ShieldCheckIcon } from '@c/icons';
import ProductInfo from '@c/products/ProductInfo';
import type {
  ChatDocument,
  ChatInput,
  ChatMessageDocument,
} from '@models/chat';
import type { OrderDocument } from '@models/order';
import type { UserDocument } from '@models/user';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import {
  createChat,
  getChatForContact,
  getNextCaseNumber,
  redactPhonesAndEmailsFromStr,
  sendMessageToChat,
} from '@util/firestore/messages/messages.service';
import { getProductById } from '@util/firestore/products';
import { getPublicUserDoc, getUserDocument } from '@util/firestore/users';
import { isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';
import BaseModal from './BaseModal';
import { useToastContext } from 'context/ToastContext';
import Select from '@ui/Select';
import FormLabel from '@c/forms/controls/FormLabel';
import { SupportState } from '@models/chat';
interface ChatModalProps {
  isOpen: boolean;
  dismiss: () => void;
  recipientUid: string;
  productId?: string;
  order?: OrderDocument;
  initialMessage?: string;
}

function ChatModal({
  isOpen,
  dismiss,
  recipientUid,
  productId,
  order,
  initialMessage,
}: ChatModalProps) {
  const { userDoc, user } = useAuth();
  const { data: recipient } = useQuery({
    queryKey: ['publicUser', recipientUid],
    queryFn: () => getPublicUserDoc({ uid: recipientUid }),
    enabled: !!recipientUid,
  });

  // ADMIN QUERY ONLY
  const { data: recipientData } = useQuery({
    queryKey: ['user-admin', recipientUid],
    queryFn: () => getUserDocument({ uid: recipientUid }),
    enabled: !!recipientUid && !!userDoc?.roles?.support,
  });
  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProductById(productId!),
    enabled: !!productId,
  });

  const [message, setMessage] = React.useState(initialMessage ?? '');
  const [selectedOption, setSelectedOption] = React.useState<string | null>(
    null
  );
  const [isSending, setIsSending] = React.useState(false);
  const { openChatDrawer } = useChat();
  const router = useRouter();
  const pathname = usePathname();
  const { showErrorToast } = useToastContext();

  async function createAndSendMessage() {
    if (!message) {
      showErrorToast('The message cannot be empty');
      return;
    }

    if (!userDoc) {
      router.push('/login');
      return;
    }

    setIsSending(true);

    try {
      // if user is not verified, redirect to verify page unless they are messaging support
      if (
        !user?.phoneNumber &&
        userDoc?.phone !== 'SKIP' &&
        recipientUid !== process.env.NEXT_PUBLIC_SUPPORT_ID &&
        !userDoc?.roles?.support
      ) {
        router.push(`/verify-phone?redirect=${encodeURI(pathname ?? '/')}`);
        dismiss();
        setIsSending(false);
        return;
      }
      if (!message) {
        setIsSending(false);
        return;
      }
      const redactedMessage =
        recipientUid === process.env.NEXT_PUBLIC_SUPPORT_ID ||
        product?.category === 'Dirt Bikes' ||
        order?.id ||
        userDoc?.roles?.support
          ? message
          : redactPhonesAndEmailsFromStr(message);
      let content = '';
      if (selectedOption) {
        content = `Support chat initiated with the following issue: ${selectedOption}. `;
      }
      content += redactedMessage;
      const messageDoc = {
        uid: userDoc?.uid ?? '',
        content,
        created_at: Date.now(),
      } as ChatMessageDocument;
      // Two cases: chat exists or chat doesn't exist
      // If chat exists, just send the message

      const existingChats = await getChatForContact({
        fields: {
          recipient_uid: recipientUid,
          product_id: productId,
          order_id: order?.id,
        },
        userDoc: userDoc!,
      });

      const existingChat = existingChats?.[0];

      let chatToOpen = existingChat;
      if (existingChat) {
        sendMessageToChat(messageDoc, existingChat, userDoc, product);
      } else {
        // If chat doesn't exist, create the chat and with the message
        const getSellerId = () => {
          if (productId) {
            if (product?.seller_id === userDoc.uid) return userDoc.uid;
            return recipientUid;
          }
          if (order) {
            // 1. buyer messaging seller -> recip
            // 2. seller messaging buyer -> user doc
            // 3. support messaging buyer -> user doc
            // 4. support message seller -> recip
            if (
              order.buyer_id === userDoc.uid ||
              order.seller_arr.includes(recipientUid)
            )
              return recipientUid;
            if (
              order.seller_arr.includes(userDoc.uid) ||
              order.buyer_id === recipientUid
            )
              return userDoc.uid;
            // shouldn't get here
            return recipientUid;
          }
          // if no productid or orderid, just return recipient id
          return recipientUid;
        };

        const uids = [userDoc.uid, recipientUid];
        const seller_id = getSellerId();
        const buyer_id = uids.filter((uid) => uid !== seller_id)[0];
        const chatDoc: Partial<ChatDocument> = {
          uids: [userDoc?.uid ?? '', recipientUid],
          buyer_id: buyer_id ?? '',
          seller_id: seller_id ?? '',
          ...(order && {
            order_id: order.id,
            order_num: order.order_num,
          }),
          messages: [messageDoc],
          product_id: productId ?? '',
          created_at: Date.now(),
          unread: {
            [recipientUid]: true,
            [userDoc?.uid ?? '']: false,
          },
          is_offer: false,
        };
        if (userDoc?.uid && userDoc.uid !== process.env.STAGING_ID) {
          chatDoc.unread = { [recipientUid]: true, [userDoc.uid]: false };
        }
        if (selectedOption) {
          chatDoc.case_num = await getNextCaseNumber();
          chatDoc.support_state = SupportState.NewTicket;
        }
        await createChat(chatDoc as ChatInput);
        chatToOpen = chatDoc as ChatDocument;
      }
      setMessage('');
      setSelectedOption(null);
      dismiss();
      if (pathname?.includes('/checkout')) return;
      if (isMobile()) {
        router.push('/dashboard/messages');
      } else if (chatToOpen) {
        openChatDrawer(chatToOpen);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showErrorToast('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  }

  function getRecipientPhone(recipient: UserDocument) {
    if (recipient.phone) {
      return recipient.phone;
    }
    if (recipient.addresses) {
      const address =
        recipient.addresses.find((address) => address.is_default) ??
        recipient.addresses[0];
      if (address && address.phone) {
        return address.phone;
      }
    }
    return 'No phone number found';
  }

  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={dismiss}
      title="Send Message"
      isFullScreen={isMobile()}
      className="p-[1.5rem]"
    >
      <div className="flex w-full flex-col gap-[2.4rem] rounded-3xl p-[1.5rem] sm:w-[50rem]">
        {recipient && (
          <div className="flex items-center gap-[1.6rem]">
            <SafeImage
              src={recipient?.photo}
              key={recipient?.photo}
              alt={'user image'}
              className="rounded-full"
              width={50}
              height={50}
            />
            <span className="text-[1.8rem] font-semibold text-brand-secondary">
              {recipient.username}
            </span>
            {recipient.is_verified && <ShieldCheckIcon />}
          </div>
        )}

        {product && (
          <div className="w-full pl-1">
            <ProductInfo product={product} />{' '}
          </div>
        )}
        {userDoc?.roles?.support && recipientData && (
          <div className="flex w-full flex-col gap-[0.8rem]">
            <span>Email: {recipientData.email}</span>
            <span>Phone: {getRecipientPhone(recipientData)}</span>
          </div>
        )}
        {recipientUid === process.env.NEXT_PUBLIC_SUPPORT_ID &&
          !order &&
          !productId && (
            <FormLabel value="Select a Topic">
              <Select
                options={[
                  {
                    label: 'Selling an Item',
                    value: 'Selling',
                    id: 'Selling',
                  },
                  {
                    label: 'Buying an Item',
                    value: 'Buying',
                    id: 'Buying',
                  },
                  {
                    label: 'My Account',
                    value: 'My Account',
                    id: 'My Account',
                  },
                  {
                    label: 'Payment',
                    value: 'Payment',
                    id: 'Payment',
                  },
                  {
                    label: 'Other',
                    value: 'Other',
                    id: 'Other',
                  },
                ]}
                onChange={(e) => setSelectedOption(e.value)}
                value={selectedOption ?? ''}
              />
            </FormLabel>
          )}
        <div className="w-full">
          <FormTextarea
            className="h-[25rem]"
            placeholder="Start typing..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className="flex w-full items-center justify-center gap-[1.6rem] font-semibold text-brand-secondary">
          <Button
            type="secondary"
            text={isSending ? 'Sending...' : 'Send'}
            leadingIcon={<SendIcon />}
            width="fluid"
            onClick={() => createAndSendMessage()}
            disabled={isSending}
            loading={isSending}
          />
        </div>
      </div>
    </BaseModal>
  );
}

export default ChatModal;
